import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import axios from 'axios';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from '@/pages/Login';
import SessionTimeoutWarning from '@/components/SessionTimeoutWarning';

// ─── Code-split all heavy authenticated pages via React.lazy ───────────────
// Previously every page (incl. KentuckyGallery's 3,307 lines, AdminTools's
// 4,000+ lines, etc.) was imported synchronously, producing one massive bundle
// that the browser had to fully parse before EVEN the login screen could
// render. On production that meant 1-3 minutes of staring at a blank/spinner
// page before login was interactable. Lazy-loading drops the initial bundle to
// just App + Login + shared deps; the per-route chunks load on first navigation.
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const DashboardOverview = lazy(() => import('@/pages/DashboardOverview'));
const Settings = lazy(() => import('@/pages/Settings'));
const RoyalScraper = lazy(() => import('@/pages/RoyalScraper'));
const TailoredScraper = lazy(() => import('@/pages/TailoredScraper'));
const KentuckyScraper = lazy(() => import('@/pages/KentuckyScraper'));
const KentuckyGallery = lazy(() => import('@/pages/KentuckyGallery'));
const ProductsGallery = lazy(() => import('@/pages/ProductsGallery'));
const ActivityLogs = lazy(() => import('@/pages/ActivityLogs'));
const MasterdataManager = lazy(() => import('@/pages/MasterdataManager'));
const FulfillmentOrders = lazy(() => import('@/pages/FulfillmentOrders'));
const AdminTools = lazy(() => import('@/pages/AdminTools'));
const KentuckyDebug = lazy(() => import('@/pages/KentuckyDebug'));
const HelpFAQ = lazy(() => import('@/pages/HelpFAQ'));
const ShopifyInventory = lazy(() => import('@/pages/ShopifyInventory'));
const ShopifyMirror = lazy(() => import('@/pages/ShopifyMirror'));
const ShopifyDriftScanner = lazy(() => import('@/pages/ShopifyDriftScanner'));
const SeasonUpdate = lazy(() => import('@/pages/SeasonUpdate'));
const ShopifyVariantCleanup = lazy(() => import('@/pages/ShopifyVariantCleanup'));
const VariationPreview = lazy(() => import('@/pages/VariationPreview'));
const DuplicateScanner = lazy(() => import('@/pages/DuplicateScanner'));
const OrdersHub = lazy(() => import('@/pages/OrdersHub'));
const Orders = lazy(() => import('@/pages/Orders'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const AuditLogs = lazy(() => import('@/pages/AuditLogs'));

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Global axios interceptors — silently handle 401 "Session invalidated" on every
// authenticated request so users don't see a burst of error toasts when their
// token is stale. The request itself still rejects so page code can fall through
// to its own error handling, but we swallow the specific "Session invalidated"
// detail so it never surfaces as a toast.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Attach the session token to raw fetch() calls aimed at our API, mirroring the axios interceptor
const _nativeFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  const url = typeof input === 'string' ? input : input?.url || '';
  const token = localStorage.getItem('admin_token');
  if (token && (url.startsWith(API) || url.startsWith('/api'))) {
    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return _nativeFetch(input, { ...init, headers, credentials: init.credentials || 'include' });
  }
  return _nativeFetch(input, init);
};

let _redirectingToLogin = false;
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    if (status === 401 && (detail === 'Session invalidated' || detail === 'Token expired' || detail === 'Invalid token')) {
      // Clear stale creds and redirect to login once (avoid N concurrent toasts)
      if (!_redirectingToLogin) {
        _redirectingToLogin = true;
        try {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_email');
          localStorage.removeItem('admin_id');
          localStorage.removeItem('admin_role');
        } catch (_e) { /* ignore */ }
        if (!window.location.pathname.startsWith('/login')) {
          window.location.replace('/login');
        }
      }
      // Swallow the error so callers don't toast "Session invalidated"
      return new Promise(() => {}); // never resolves — page will be redirected
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const verifySession = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setLoading(false); return; }

    try {
      await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setIsAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 401) {
        // Try refresh once
        try {
          const refreshResp = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          if (refreshResp.data?.token) {
            localStorage.setItem('admin_token', refreshResp.data.token);
            setIsAuthenticated(true);
          } else {
            handleLogout();
          }
        } catch {
          handleLogout();
        }
      }
      // Network error → keep current state silently
    }
    setLoading(false);
  }, []);

  useEffect(() => { verifySession(); }, [verifySession]);

  const handleLogin = () => setIsAuthenticated(true);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
    } catch { /* ignore */ }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_id');
    localStorage.removeItem('admin_role');
    setIsAuthenticated(false);
  };

  const PrivateRoute = ({ children }) => {
    if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div className="spinner"></div></div>;
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div className="spinner"></div></div>;

  return (
    <div className="App">
      {isAuthenticated && <SessionTimeoutWarning onLogout={handleLogout} />}
      <BrowserRouter>
        <Suspense fallback={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
            <div className="spinner"></div>
          </div>
        }>
          <Routes>
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/overview" /> : <Login onLogin={handleLogin} />
            } />
            <Route path="/" element={
              <PrivateRoute><Dashboard onLogout={handleLogout} /></PrivateRoute>
            }>
              <Route index element={<Navigate to="/overview" />} />
              <Route path="overview" element={<DashboardOverview />} />
              <Route path="products" element={<ProductsGallery />} />
              <Route path="kentucky-gallery" element={<KentuckyGallery />} />
              <Route path="masterdata" element={<MasterdataManager />} />
              <Route path="kentucky-scraper" element={<KentuckyScraper />} />
              <Route path="royal-scraper" element={<RoyalScraper />} />
              <Route path="tailored-scraper" element={<TailoredScraper />} />
              <Route path="shopify-inventory" element={<ShopifyInventory />} />
              <Route path="shopify-mirror" element={<ShopifyMirror />} />
              <Route path="shopify-drift" element={<ShopifyDriftScanner />} />
              <Route path="season-update" element={<SeasonUpdate />} />
              <Route path="shopify-variant-cleanup" element={<ShopifyVariantCleanup />} />
              <Route path="variation-preview" element={<VariationPreview />} />
              <Route path="duplicate-scanner" element={<DuplicateScanner />} />
              <Route path="fulfillment-orders" element={<FulfillmentOrders />} />
              <Route path="orders-hub" element={<OrdersHub />} />
              <Route path="orders" element={<Orders />} />
              <Route path="kentucky-debug" element={<KentuckyDebug />} />
              <Route path="activity-logs" element={<ActivityLogs />} />
              <Route path="admin-tools" element={<AdminTools />} />
              <Route path="user-management" element={<UserManagement />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="settings" element={<Settings />} />
              <Route path="help" element={<HelpFAQ />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
